import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Building, 
  Crown, 
  Zap, 
  Globe, 
  Star, 
  Settings, 
  Users, 
  CreditCard,
  Shield,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Mail,
  UserPlus,
  RefreshCw,
  Trash2,
  Loader2,
  DollarSign,
  Calendar,
  Plus
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { useAdminCheck } from "@/hooks/use-admin-check";
import { StripeService } from "@/services/stripe-service";
import { DomainVerificationModal } from "@/components/DomainVerificationModal";
import { DNSVerificationService } from "@/services/dns-verification-service";
import { WorkspaceService } from "@/services/workspace-service";
import { InvitationService, InvitationRequest } from "@/services/invitation-service";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { getWorkspaceUrlFromObject } from "@/utils/tenant-utils";

interface WorkspaceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: {
    id: string;
    name: string;
    slug: string;
    plan_type?: string;
    domain?: string;
    domain_verified?: boolean;
    is_default?: boolean;
  };
  onWorkspaceUpdate?: (updatedWorkspace: any) => void;
}

export const WorkspaceSettingsModal: React.FC<WorkspaceSettingsModalProps> = ({
  isOpen,
  onClose,
  workspace,
  onWorkspaceUpdate
}) => {
  const { user, setUser, openWorkspaceModal } = useAuth();
  const { toast } = useToast();
  const { isAdmin: isAdminUser } = useAdminCheck();
  
  // Admin user check now handled by useAdminCheck hook
  
  // State
  const [loading, setLoading] = useState(false);
  const [workspaceName, setWorkspaceName] = useState(workspace.name);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [billingInfo, setBillingInfo] = useState<any>(null);
  const [domainVerifications, setDomainVerifications] = useState<any[]>([]);
  const [isDefaultWorkspace, setIsDefaultWorkspace] = useState(workspace.is_default || false);
  const [userWorkspaces, setUserWorkspaces] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Member management state
  const [inviteEmails, setInviteEmails] = useState<string>('');
  const [inviteRole, setInviteRole] = useState<'workspace_admin' | 'workspace_member'>('workspace_member');
  const [inviteMessage, setInviteMessage] = useState<string>('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState<any[]>([]);
  const [invitationLimits, setInvitationLimits] = useState<{
    canSend: boolean;
    currentUsers: number;
    maxUsers: number;
    pendingInvitations: number;
    planName: string;
  } | null>(null);

  // User profile settings state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // Load workspace data when modal opens
  useEffect(() => {
    if (isOpen) {
      setWorkspaceName(workspace.name);
      setIsDefaultWorkspace(workspace.is_default || false);
      loadWorkspaceData();
    }
  }, [isOpen, workspace]);

  const loadWorkspaceData = async () => {
    try {
      setLoading(true);
      
      // Load user workspaces to check default status and user role
      console.log('🔍 WorkspaceSettingsModal: Loading user workspaces...');
      const workspaces = await WorkspaceService.getUserWorkspaces();
      console.log('📋 WorkspaceSettingsModal: Loaded workspaces:', workspaces.length, workspaces.map(w => ({ id: w.id, name: w.name, is_default: w.is_default })));
      setUserWorkspaces(workspaces);
      
      // Check if current workspace is default and get user role
      const currentWorkspace = workspaces.find(w => w.id === workspace.id);
      if (currentWorkspace) {
        setIsDefaultWorkspace(currentWorkspace.is_default);
        setUserRole(currentWorkspace.role);
      } else {
        console.warn('⚠️ Current workspace not found in user workspaces list!', {
          currentWorkspaceId: workspace.id,
          availableWorkspaces: workspaces.map(w => ({ id: w.id, name: w.name }))
        });
      }
      
      // Load billing information (only for workspace admins)
      if (currentWorkspace && currentWorkspace.role === 'workspace_admin') {
        try {
          const billing = await StripeService.getWorkspaceBilling(workspace.id);
          setBillingInfo(billing);
        } catch (billingError) {
          console.warn('Failed to load billing info (admin only):', billingError);
          setBillingInfo(null);
        }
      } else {
        console.log('🚫 Skipping billing load - user is not workspace admin');
        setBillingInfo(null);
      }

      // Load domain verifications if Premium/Enterprise
      if (['premium', 'enterprise'].includes(workspace.plan_type || '')) {
        const verifications = await DNSVerificationService.getWorkspaceDomainVerifications(workspace.id);
        setDomainVerifications(verifications);
      }

      // Load member data
      await loadMemberData();
    } catch (error) {
      console.error('Error loading workspace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMemberData = async () => {
    try {
      setLoadingInvitations(true);
      
      // Load pending invitations
      const invitations = await InvitationService.getPendingInvitations(workspace.id);
      setPendingInvitations(invitations);
      
      // Load current workspace members
      const members = await getWorkspaceMembers(workspace.id);
      setWorkspaceMembers(members);

      // Load invitation limits
      try {
        const limits = await InvitationService.canSendInvitations(workspace.id, 0);
        setInvitationLimits(limits);
      } catch (error) {
        console.error('Error loading invitation limits:', error);
        setInvitationLimits(null);
      }
    } catch (error) {
      console.error('Error loading member data:', error);
    } finally {
      setLoadingInvitations(false);
    }
  };

  // Helper function to get workspace members
  const getWorkspaceMembers = async (workspaceId: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from('workspace_memberships')
        .select(`
          id,
          role,
          status,
          joined_at,
          invited_at,
          watt_users:user_id (
            id,
            display_name,
            email,
            job_title
          )
        `)
        .eq('workspace_id', workspaceId)
        .eq('status', 'active')
        .order('joined_at', { ascending: true });

      if (error) {
        console.error('Error fetching workspace members:', error);
        return [];
      }

      return data?.map((member: any) => ({
        id: member.id,
        role: member.role,
        status: member.status,
        joined_at: member.joined_at,
        invited_at: member.invited_at,
        user: member.watt_users
      })) || [];
    } catch (error) {
      console.error('Error in getWorkspaceMembers:', error);
      return [];
    }
  };

  const handleSaveBasicSettings = async () => {
    try {
      setLoading(true);
      
      // Update workspace name in database
      const { error } = await (supabase as any)
        .from('watt_workspaces')
        .update({ name: workspaceName })
        .eq('id', workspace.id);

      if (error) {
        throw error;
      }
      
      toast({
        title: "Settings Updated",
        description: "Workspace settings have been saved.",
      });
      
      // Notify parent component of the update
      if (onWorkspaceUpdate) {
        onWorkspaceUpdate({ ...workspace, name: workspaceName });
      }
      
      // Reload workspace data to reflect changes
      await loadWorkspaceData();
    } catch (error) {
      console.error('Error updating workspace:', error);
      toast({
        title: "Error",
        description: "Failed to update workspace settings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetAsDefault = async (workspaceId: string) => {
    try {
      setLoading(true);
      
      const success = await WorkspaceService.setDefaultWorkspace(workspaceId);
      
      if (success) {
        // Find the workspace that was set as default
        const targetWorkspace = userWorkspaces.find(ws => ws.id === workspaceId);
        const workspaceName = targetWorkspace?.name || 'Selected workspace';
        
        // Update local state if the current workspace was set as default
        if (workspaceId === workspace.id) {
          setIsDefaultWorkspace(true);
        } else {
          setIsDefaultWorkspace(false);
        }
        
        toast({
          title: "Default Workspace Updated",
          description: `${workspaceName} is now your default workspace.`,
        });
        
        // Reload workspace data to refresh the state
        await loadWorkspaceData();
      } else {
        throw new Error('Failed to set default workspace');
      }
    } catch (error) {
      console.error('Error setting default workspace:', error);
      toast({
        title: "Error",
        description: "Failed to update default workspace.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMembers = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmails.trim()) return;

    setInviteLoading(true);

    try {
      // Parse emails (support comma, semicolon, or newline separated)
      const emailList = inviteEmails
        .split(/[,;\n]/)
        .map(email => email.trim())
        .filter(email => email && email.includes('@'));

      if (emailList.length === 0) {
        toast({
          title: "Error",
          description: "Please enter valid email addresses",
          variant: "destructive"
        });
        setInviteLoading(false);
        return;
      }

      // Check for duplicates using pendingInvitations
      const alreadyPending = new Set(
        pendingInvitations.map(inv => inv.email.toLowerCase())
      );
      const emailsToInvite = emailList.filter(email => !alreadyPending.has(email.toLowerCase()));
      const duplicates = emailList.filter(email => alreadyPending.has(email.toLowerCase()));

      if (emailsToInvite.length === 0) {
        toast({
          title: "Error",
          description: "All provided emails already have pending invitations",
          variant: "destructive"
        });
        setInviteLoading(false);
        return;
      }

      const invitationRequest: InvitationRequest = {
        emails: emailsToInvite,
        role: inviteRole,
        message: inviteMessage.trim() || undefined
      };

      const result = await InvitationService.sendInvitations(workspace.id, invitationRequest);

      // Handle limit errors
      if (result.error) {
        toast({
          title: "Cannot Send Invitations",
          description: result.error,
          variant: "destructive"
        });
        setInviteLoading(false);
        return;
      }

      if (result.success.length > 0) {
        toast({
          title: "Success",
          description: `Sent ${result.success.length} invitation${result.success.length > 1 ? 's' : ''} successfully`
        });
        
        // Clear form on success
        setInviteEmails('');
        setInviteMessage('');
        
        // Reload invitations
        await loadMemberData();
      }

      if (result.failed.length > 0 || duplicates.length > 0) {
        const totalFailed = result.failed.length + duplicates.length;
        toast({
          title: "Partial Success",
          description: `${totalFailed} invitation${totalFailed > 1 ? 's' : ''} failed to send`,
          variant: "destructive"
        });
      }

    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred while sending invitations",
        variant: "destructive"
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      const success = await InvitationService.resendInvitation(invitationId);
      if (success) {
        toast({
          title: "Success",
          description: "Invitation resent successfully"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to resend invitation",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resend invitation",
        variant: "destructive"
      });
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      const success = await InvitationService.revokeInvitation(invitationId);
      if (success) {
        toast({
          title: "Success",
          description: "Invitation revoked successfully"
        });
        await loadMemberData();
      } else {
        toast({
          title: "Error",
          description: "Failed to revoke invitation",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to revoke invitation",
        variant: "destructive"
      });
    }
  };

  const handleSaveUserProfile = async () => {
    if (!user) return;

    // Validate password if provided
    if (newPassword) {
      if (newPassword.length < 6) {
        toast({
          title: "Invalid Password",
          description: "Password must be at least 6 characters long.",
          variant: "destructive",
        });
        return;
      }

      if (newPassword !== confirmPassword) {
        toast({
          title: "Password Mismatch",
          description: "New password and confirm password do not match.",
          variant: "destructive",
        });
        return;
      }
    }

    setProfileLoading(true);

    try {
      // Update display name in watt_users table
      const { error: profileError } = await supabase
        .from('watt_users')
        .update({ display_name: user.display_name })
        .eq('id', user.id);

      if (profileError) {
        throw profileError;
      }

      // Update password if provided
      if (newPassword) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (passwordError) {
          throw passwordError;
        }

        // Clear password fields
        setNewPassword('');
        setConfirmPassword('');
      }

      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });

    } catch (error) {
      console.error('Error updating user profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAddWorkspace = () => {
    openWorkspaceModal();
    onClose(); // Close settings modal when opening workspace creation modal
  };

  const getPlanBadge = (planType: string) => {
    const planConfig = {
      free: { label: 'Free', icon: Star, variant: 'secondary' as const },
      basic: { label: 'Basic', icon: Zap, variant: 'default' as const },
      premium: { label: 'Premium', icon: Crown, variant: 'default' as const },
      enterprise: { label: 'Enterprise', icon: Crown, variant: 'destructive' as const }
    };

    const config = planConfig[planType as keyof typeof planConfig] || planConfig.free;
    const IconComponent = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <IconComponent className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'workspace_admin':
        return <Badge variant="destructive">Admin</Badge>;
      case 'workspace_member':
        return <Badge variant="secondary">Member</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate number of visible tabs for grid layout
  const getTabCount = () => {
    let count = 1; // General tab is always visible
    if (isWorkspaceAdmin) count += 2; // Domains + Billing for admins
    if (canManageMembers) count += 1; // Members for admins only
    return count;
  };

  const canUseDNS = ['premium', 'enterprise'].includes(workspace.plan_type || '');
  const isWorkspaceAdmin = userRole === 'workspace_admin';
  const canManageMembers = userRole === 'workspace_admin';
  const hasVerifiedDomain = workspace.domain_verified;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Workspace Settings
            </DialogTitle>
            <DialogDescription>
              Manage your workspace configuration and features.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="general" className="gap-2">
                <Settings className="w-4 h-4" />
                General
              </TabsTrigger>
              {isWorkspaceAdmin && (
                <TabsTrigger value="domains" className="gap-2">
                  <Globe className="w-4 h-4" />
                  Domains
                </TabsTrigger>
              )}
              {/* Billing tab temporarily disabled for beta
              {isWorkspaceAdmin && (
                <TabsTrigger value="billing" className="gap-2">
                  <CreditCard className="w-4 h-4" />
                  Billing (Beta Version)
                </TabsTrigger>
              )}
              */}
              {(isWorkspaceAdmin || canManageMembers) && (
                <TabsTrigger value="members" className="gap-2">
                  <Users className="w-4 h-4" />
                  Members
                </TabsTrigger>
              )}
            </TabsList>

            {/* General Settings */}
            <TabsContent value="general" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Basic Information</CardTitle>
                  <CardDescription>
                    Update your workspace name and basic settings.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="workspace-name">Workspace Name</Label>
                    <Input
                      id="workspace-name"
                      value={workspaceName}
                      onChange={(e) => setWorkspaceName(e.target.value)}
                      placeholder="Enter workspace name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Workspace URL</Label>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-sm flex-1">
                        {workspace.slug}.wattfactory.tech
                      </code>
                      <Badge variant="outline">Subdomain</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your workspace subdomain cannot be changed after creation.
                    </p>
                  </div>



                  <div className="space-y-2">
                    <Label>Your Workspaces</Label>
                    <div className="space-y-2">
                      {userWorkspaces.length > 0 && (
                        <div className="p-3 border rounded-lg">
                          <div className="text-sm font-medium mb-3">
                            {userWorkspaces.length > 1 ? 'Switch Workspace' : 'Current Workspace'}
                          </div>
                          <div className="space-y-3">
                            {userWorkspaces
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((ws) => {
                                // Check if this is the current workspace by comparing slug (more reliable than ID for fallbacks)
                                const isCurrentWorkspace = ws.id === workspace.id || ws.slug === workspace.slug;
                                
                                return (
                              <div key={ws.id} className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {/* Favorite Star - clickable to set as default */}
                                  <button
                                    onClick={() => handleSetAsDefault(ws.id)}
                                    className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                                      ws.is_default ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-400'
                                    }`}
                                    title={ws.is_default ? 'Default workspace (auto-login)' : 'Set as default workspace'}
                                  >
                                    <Star className={`w-4 h-4 ${ws.is_default ? 'fill-yellow-500' : ''}`} />
                                  </button>
                                  
                                  <Building className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-medium">{ws.name}</span>
                                  
                                  <Badge variant="outline" className="text-xs">
                                    {ws.role.replace('workspace_', '')}
                                  </Badge>
                                  
                                  {ws.is_default && (
                                    <Badge variant="secondary" className="text-xs">
                                      Default
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Switch Button - disabled for current workspace */}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  disabled={isCurrentWorkspace}
                                  onClick={() => {
                                    if (!isCurrentWorkspace) {
                                      const workspaceUrl = getWorkspaceUrlFromObject(ws);
                                      window.location.href = workspaceUrl;
                                    }
                                  }}
                                >
                                  {isCurrentWorkspace ? 'Current' : 'Switch'}
                                </Button>
                              </div>
                            )})}
                          </div>
                          <div className="mt-3 pt-3 border-t">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              Click the star to set your default workspace (auto-login destination)
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleSaveBasicSettings} disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    
                    {isAdminUser && (
                      <Button 
                        variant="outline" 
                        onClick={handleAddWorkspace}
                        className="flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Workspace
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* User Profile Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">User Profile</CardTitle>
                  <CardDescription>
                    Update your display name and password.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="display-name">Display Name</Label>
                    <Input
                      id="display-name"
                      value={user?.display_name || ''}
                      onChange={(e) => {
                        // Update local state immediately for better UX
                        if (user) {
                          setUser({ ...user, display_name: e.target.value });
                        }
                      }}
                      placeholder="Enter your display name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={user?.email || ''}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Email address cannot be changed. Contact support if needed.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter new password"
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank to keep current password.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Confirm new password"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <Button onClick={handleSaveUserProfile} disabled={profileLoading}>
                    {profileLoading ? 'Saving...' : 'Save Profile Changes'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Domain Settings - Admin Only */}
            {isWorkspaceAdmin && (
              <TabsContent value="domains" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Custom Domain
                  </CardTitle>
                  <CardDescription>
                    Use your own domain for your workspace instead of the subdomain.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!canUseDNS ? (
                    <Alert>
                      <Crown className="w-4 h-4" />
                      <AlertDescription>
                        Custom domains are available for Premium and Enterprise plans. 
                        <Button variant="link" className="p-0 h-auto font-semibold ml-1">
                          Upgrade your plan
                        </Button> to use this feature.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      {/* Current Domain Status */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="font-medium">Current Access URL</div>
                            <div className="text-sm text-muted-foreground">
                              {workspace.domain && workspace.domain_verified ? (
                                <span className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                  {workspace.domain}
                                </span>
                              ) : (
                                <span>{workspace.slug}.wattfactory.tech</span>
                              )}
                            </div>
                          </div>
                          {workspace.domain && workspace.domain_verified && (
                            <Badge variant="default" className="gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Verified
                            </Badge>
                          )}
                        </div>

                        {/* Domain Verifications List */}
                        {domainVerifications.length > 0 && (
                          <div className="space-y-2">
                            <Label>Domain Verifications</Label>
                            {domainVerifications.map((verification) => (
                              <div key={verification.id} className="flex items-center justify-between p-2 border rounded">
                                <div>
                                  <div className="font-medium">{verification.domain}</div>
                                  <div className="text-xs text-muted-foreground">
                                    Status: {verification.status} • Expires: {new Date(verification.expires_at).toLocaleDateString()}
                                  </div>
                                </div>
                                <Badge variant={
                                  verification.status === 'verified' ? 'default' :
                                  verification.status === 'pending' ? 'secondary' :
                                  verification.status === 'failed' ? 'destructive' : 'outline'
                                }>
                                  {verification.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={() => setShowDomainModal(true)}
                        className="gap-2"
                        disabled={hasVerifiedDomain || !isWorkspaceAdmin}
                      >
                        <Globe className="w-4 h-4" />
                        {hasVerifiedDomain 
                          ? 'Domain Already Verified' 
                          : workspace.domain 
                            ? 'Manage Domain' 
                            : 'Set Up Custom Domain'
                        }
                      </Button>

                      {/* Show access control messages */}
                      {!isWorkspaceAdmin && (
                        <Alert>
                          <Shield className="w-4 h-4" />
                          <AlertDescription>
                            Only workspace administrators can set up custom domains.
                          </AlertDescription>
                        </Alert>
                      )}

                      {hasVerifiedDomain && isWorkspaceAdmin && (
                        <Alert>
                          <CheckCircle className="w-4 h-4" />
                          <AlertDescription>
                            Your custom domain <strong>{workspace.domain}</strong> is verified and active. 
                            Domain settings are locked for security.
                          </AlertDescription>
                        </Alert>
                      )}

                      <div className="bg-muted p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Custom Domain Benefits:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Professional branded workspace URL</li>
                          <li>• Enhanced security with domain verification</li>
                          <li>• Automatic SSL certificate</li>
                          <li>• Better user experience for your team</li>
                        </ul>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            )}

            {/* Billing Settings - Admin Only */}
            {isWorkspaceAdmin && (
              <TabsContent value="billing" className="space-y-4">
                {/* Current Plan Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Crown className="w-5 h-5" />
                      Current Plan
                    </CardTitle>
                    <CardDescription>
                      View your current workspace plan and upgrade options.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="font-medium">Workspace Plan</div>
                          <div className="text-sm text-muted-foreground">
                            {workspace.plan_type === 'free' && 'Basic features with limited storage'}
                            {workspace.plan_type === 'basic' && 'Enhanced features for small teams'}
                            {workspace.plan_type === 'premium' && 'Advanced features including custom domains'}
                            {workspace.plan_type === 'enterprise' && 'Full enterprise features and support'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getPlanBadge(workspace.plan_type || 'free')}
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled
                          onClick={async () => {
                            try {
                              if (!user) return;
                              
                              const sessionId = await StripeService.createCheckoutSession({
                                planType: 'basic', // Default to basic for upgrade
                                workspaceId: workspace.id,
                                workspaceSlug: workspace.slug,
                                customerEmail: user.email,
                                purchaseType: 'subscription'
                              });
                              
                              // Redirect to Stripe checkout
                              await StripeService.redirectToCheckout(sessionId);
                            } catch (error) {
                              console.error('Error upgrading plan:', error);
                              toast({
                                title: "Error",
                                description: "Failed to start upgrade process. Please try again.",
                                variant: "destructive"
                              });
                            }
                          }}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Upgrade Plan (Coming Soon)
                        </Button>
                      </div>
                    </div>

                    {/* Plan Features */}
                    <div className="bg-muted p-4 rounded-lg">
                      <h4 className="font-semibold mb-2">Plan Features:</h4>
                      <ul className="text-sm space-y-1">
                        {workspace.plan_type === 'free' && (
                          <>
                            <li>• Up to 5 team members</li>
                            <li>• 1GB storage</li>
                            <li>• 3 data configurations</li>
                            <li>• Basic support</li>
                          </>
                        )}
                        {workspace.plan_type === 'basic' && (
                          <>
                            <li>• Up to 10 team members</li>
                            <li>• 5GB storage</li>
                            <li>• 10 data configurations</li>
                            <li>• AI credits included</li>
                            <li>• Priority support</li>
                          </>
                        )}
                        {workspace.plan_type === 'premium' && (
                          <>
                            <li>• Up to 50 team members</li>
                            <li>• 10GB storage</li>
                            <li>• Unlimited data configurations</li>
                            <li>• Custom DNS</li>
                            <li>• Advanced AI credits</li>
                            <li>• Priority support</li>
                          </>
                        )}
                        {workspace.plan_type === 'enterprise' && (
                          <>
                            <li>• Unlimited team members</li>
                            <li>• Unlimited storage</li>
                            <li>• Custom subdomains</li>
                            <li>• Custom integrations</li>
                            <li>• Dedicated support</li>
                            <li>• SLA guarantees</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Credits Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      AI Credits
                    </CardTitle>
                    <CardDescription>
                      Track your AI usage and credits
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span>Credits Used</span>
                          <span>{billingInfo?.ai_credits_used || 0}</span>
                        </div>
                        <Progress 
                          value={billingInfo?.ai_credits_purchased ? 
                            ((billingInfo.ai_credits_used / billingInfo.ai_credits_purchased) * 100) : 0
                          } 
                          className="h-2"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-primary">
                            {billingInfo?.ai_credits_purchased || 0}
                          </div>
                          <div className="text-xs text-muted-foreground">Purchased</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {(billingInfo?.ai_credits_purchased || 0) - (billingInfo?.ai_credits_used || 0)}
                          </div>
                          <div className="text-xs text-muted-foreground">Remaining</div>
                        </div>
                      </div>

                      <Button 
                        className="w-full"
                        disabled
                        onClick={async () => {
                          try {
                            if (!user) return;
                            
                            const sessionId = await StripeService.createCheckoutSession({
                              planType: 'basic', // Not used for credits
                              workspaceId: workspace.id,
                              workspaceSlug: workspace.slug,
                              customerEmail: user.email,
                              purchaseType: 'ai_credits',
                              creditsAmount: 100 // Default to 100 credits
                            });
                            
                            // Redirect to Stripe checkout
                            await StripeService.redirectToCheckout(sessionId);
                          } catch (error) {
                            console.error('Error buying credits:', error);
                            toast({
                              title: "Error",
                              description: "Failed to start credit purchase. Please try again.",
                              variant: "destructive"
                            });
                          }
                        }}
                      >
                        Buy More Credits (Coming Soon)
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Billing Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Billing Information
                    </CardTitle>
                    <CardDescription>
                      Manage your subscription and billing information.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {billingInfo ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span>Subscription Status:</span>
                          <Badge variant={billingInfo.subscription_status === 'active' ? 'default' : 'destructive'}>
                            {billingInfo.subscription_status}
                          </Badge>
                        </div>

                        {billingInfo.subscription_current_period_end && (
                          <div className="flex items-center justify-between">
                            <span>Next Billing Date:</span>
                            <span className="text-sm">
                              {new Date(billingInfo.subscription_current_period_end).toLocaleDateString()}
                            </span>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          <Button 
                            variant="outline"
                            disabled
                            onClick={async () => {
                              try {
                                if (!billingInfo?.stripe_customer_id) {
                                  toast({
                                    title: "No Billing Account",
                                    description: "You need to upgrade to a paid plan first.",
                                    variant: "destructive"
                                  });
                                  return;
                                }
                                
                                const portalUrl = await StripeService.createPortalSession({
                                  workspaceId: workspace.id,
                                  customerId: billingInfo.stripe_customer_id
                                });
                                
                                window.location.href = portalUrl;
                              } catch (error) {
                                console.error('Error opening billing portal:', error);
                                toast({
                                  title: "Error",
                                  description: "Failed to open billing portal. Please try again.",
                                  variant: "destructive"
                                });
                              }
                            }}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Manage Subscription (Coming Soon)
                          </Button>
                          <Button variant="outline" disabled>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Update Payment Method (Coming Soon)
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No billing information available</p>
                        <Button 
                          className="mt-4"
                          disabled
                          onClick={async () => {
                            try {
                              if (!user) return;
                              
                              const sessionId = await StripeService.createCheckoutSession({
                                planType: 'basic', // Default to basic for upgrade
                                workspaceId: workspace.id,
                                workspaceSlug: workspace.slug,
                                customerEmail: user.email,
                                purchaseType: 'subscription'
                              });
                              
                              // Redirect to Stripe checkout
                              await StripeService.redirectToCheckout(sessionId);
                            } catch (error) {
                              console.error('Error upgrading plan:', error);
                              toast({
                                title: "Error",
                                description: "Failed to start upgrade process. Please try again.",
                                variant: "destructive"
                              });
                            }
                          }}
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade Plan (Coming Soon)
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Billing History */}
                <Card>
                  <CardHeader>
                    <CardTitle>Billing History</CardTitle>
                    <CardDescription>
                      Recent invoices and payments
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {billingInfo?.billing_history?.length ? (
                      <div className="space-y-4">
                        {billingInfo.billing_history.map((invoice: any) => (
                          <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <DollarSign className="w-5 h-5 text-primary" />
                              <div>
                                <div className="font-medium">
                                  {invoice.plan_type} Plan
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {new Date(invoice.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">
                                ${(invoice.amount_paid / 100).toFixed(2)}
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {invoice.payment_status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No billing history yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Members Settings - Admin & Lead Only */}
            {(isWorkspaceAdmin || canManageMembers) && (
              <TabsContent value="members" className="space-y-4">
              {/* Invite New Members */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserPlus className="w-5 h-5" />
                    Invite New Members
                  </CardTitle>
                  <CardDescription>
                    Send invitations to new team members to join this workspace.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Invitation Limits Display */}
                  {invitationLimits && (
                    <div className="mb-4 p-3 bg-muted rounded-lg">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">User Limits ({invitationLimits.planName} Plan)</span>
                        <span className={`font-medium ${invitationLimits.canSend ? 'text-green-600' : 'text-red-600'}`}>
                          {invitationLimits.currentUsers} / {invitationLimits.maxUsers === -1 ? '∞' : invitationLimits.maxUsers} users
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        {invitationLimits.pendingInvitations} pending invitations
                        {!invitationLimits.canSend && (
                          <span className="block text-red-600 mt-1">
                            Cannot send more invitations. Upgrade your plan to invite more users.
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleInviteMembers} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="invite-emails">Email Addresses</Label>
                      <Textarea
                        id="invite-emails"
                        value={inviteEmails}
                        onChange={(e) => setInviteEmails(e.target.value)}
                        placeholder="Enter email addresses (one per line, or comma/semicolon separated)"
                        className="min-h-[80px]"
                        disabled={inviteLoading || (invitationLimits && !invitationLimits.canSend)}
                      />
                      <p className="text-xs text-muted-foreground">
                        You can enter multiple email addresses separated by commas, semicolons, or new lines.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="invite-role">Role</Label>
                      <Select value={inviteRole} onValueChange={(value: any) => setInviteRole(value)} disabled={inviteLoading || (invitationLimits && !invitationLimits.canSend)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="workspace_member">Member - Can contribute and upload data</SelectItem>
                          <SelectItem value="workspace_admin">Admin - Full workspace control</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="invite-message">Custom Message (Optional)</Label>
                      <Textarea
                        id="invite-message"
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        placeholder="Add a personal message to the invitation email..."
                        className="min-h-[60px]"
                        disabled={inviteLoading || (invitationLimits && !invitationLimits.canSend)}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={inviteLoading || !inviteEmails.trim() || (invitationLimits && !invitationLimits.canSend)} 
                      className="gap-2"
                    >
                      {inviteLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending Invitations...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          Send Invitations
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Pending Invitations */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Pending Invitations
                  </CardTitle>
                  <CardDescription>
                    Manage invitations that haven't been accepted yet.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingInvitations ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin mr-2" />
                      <span className="text-muted-foreground">Loading invitations...</span>
                    </div>
                  ) : pendingInvitations.length === 0 ? (
                    <div className="text-center py-6">
                      <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No pending invitations</p>
                      <p className="text-sm text-muted-foreground">Invite team members to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pendingInvitations.map((invitation) => (
                        <div key={invitation.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{invitation.email}</span>
                              {getRoleBadge(invitation.role)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Invited {formatDate(invitation.created_at)} • Expires {formatDate(invitation.expires_at)}
                            </div>
                            {invitation.inviter && (
                              <div className="text-xs text-muted-foreground">
                                By {invitation.inviter.display_name || invitation.inviter.email}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResendInvitation(invitation.id)}
                              className="gap-1"
                              title="Resend invitation"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Resend
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRevokeInvitation(invitation.id)}
                              className="gap-1 text-destructive hover:text-destructive"
                              title="Revoke invitation"
                            >
                              <Trash2 className="w-3 h-3" />
                              Revoke
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Current Members */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Current Members
                  </CardTitle>
                  <CardDescription>
                    View and manage current workspace members.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingInvitations ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin mr-2" />
                      <span className="text-muted-foreground">Loading members...</span>
                    </div>
                  ) : workspaceMembers.length === 0 ? (
                    <div className="text-center py-6">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No members found</p>
                      <p className="text-sm text-muted-foreground">This workspace doesn't have any active members yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {workspaceMembers.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">
                                {member.user?.display_name || member.user?.email || 'Unknown User'}
                              </span>
                              {getRoleBadge(member.role)}
                              {member.user?.id === user?.id && (
                                <Badge variant="outline" className="text-xs">You</Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {member.user?.email}
                            </div>
                            {member.user?.job_title && (
                              <div className="text-xs text-muted-foreground">
                                {member.user.job_title}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              Joined {formatDate(member.joined_at || member.invited_at)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Only show actions for admins and not for current user */}
                            {isWorkspaceAdmin && member.user?.id !== user?.id && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1"
                                  title="Change role"
                                  disabled
                                >
                                  <Settings className="w-3 h-3" />
                                  Manage
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1 text-destructive hover:text-destructive"
                                  title="Remove member"
                                  disabled
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Remove
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Domain Verification Modal */}
      <DomainVerificationModal
        isOpen={showDomainModal}
        onClose={() => {
          setShowDomainModal(false);
          loadWorkspaceData(); // Reload data after domain changes
        }}
        workspaceId={workspace.id}
        currentPlan={workspace.plan_type || 'free'}
      />
    </>
  );
}; 